import { LeftOutlined } from "@ant-design/icons";
import { Tabs } from "antd";
import { useNavigate } from "react-router-dom";
import "./ModuleHeader.css";

/**
 * ModuleHeader — Reusable black title banner + tab bar
 *
 * Used by every module page. Renders a black banner with an optional "<" back
 * control, the module title, and an optional tab bar below it.
 *
 * Back button (matches admin screen designs):
 *   - Shown by default (`showBack` defaults to true)
 *   - Dashboard should pass `showBack={false}`
 *   - Nested pages pass `backTo="/settings"` (or `onBack`)
 *
 * Usage:
 *   <ModuleHeader
 *     title="Delivery Partners"
 *     tabs={[
 *       { key: "rider-upload", label: "Rider Upload" },
 *       { key: "documents", label: "Document Approval" },
 *     ]}
 *     activeTab={activeTab}
 *     onTabChange={setActiveTab}
 *   />
 */
export default function ModuleHeader({
  title,
  subtitle,
  onTitleClick,
  onBack,
  backTo,
  showBack = true,
  tabs = [],
  activeTab,
  onTabChange,
  extra,
}) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(backTo || "/dashboard");
  };

  return (
    <div className="module-header">
      {/* Black title banner */}
      <div className="module-header-banner">
        <div className="module-header-titles">
          {showBack && (
            <button
              type="button"
              className="module-header-back"
              onClick={handleBack}
              aria-label="Back"
            >
              <LeftOutlined />
            </button>
          )}
          <span
            className={`module-header-title${onTitleClick ? " clickable" : ""}`}
            onClick={onTitleClick}
          >
            {title}
          </span>
          {subtitle && (
            <>
              <span className="module-header-divider">/</span>
              <span className="module-header-subtitle">{subtitle}</span>
            </>
          )}
        </div>
        {extra && <div className="module-header-extra">{extra}</div>}
      </div>

      {/* Tab bar */}
      {tabs.length > 0 && (
        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          items={tabs}
          className="module-header-tabs"
        />
      )}
    </div>
  );
}
